/**
 * Feature Enablement, without React: who the table is about, which toggles
 * have moved since it loaded, and what one Save sends.
 *
 * The screen holds every change locally until Save, because the server writes
 * a whole screenful in one transaction (`PUT /console/features/values`) and a
 * half-applied save is worse than none. So the job here is to keep a draft
 * beside the loaded rows, and to turn the difference between them into one
 * request — carrying each row's `valueVersion`, which is what makes a save
 * refuse rather than overwrite somebody else's change.
 *
 * The server checks all of it again. Nothing here is a guard; it decides what
 * to offer, and catches mistakes before a round trip.
 */

import type { CustomerSettingSource } from "@wallyo/contracts/console/accounts";
import type { SettingCategory } from "@wallyo/contracts/settings";
import type {
  CustomerFeatureListItem,
  FeatureSubject,
  SaveCustomerFeaturesRequest,
} from "@wallyo/contracts/console/configuration";

import { t, type MessageId } from "../../i18n";
import { REASON_MIN_LENGTH } from "../team/team-writes";

// ── Who the table is about ───────────────────────────────────────────────

/** What the subject bar holds while someone is typing into it. */
export type SubjectFields = {
  kind: FeatureSubject["kind"];
  /** As typed. Digits only, so "one number" is what it looks like. */
  uuid: string;
};

export const EVERY_SUBJECT_KIND = ["allClients", "allPros", "user"] as const;

export function emptySubject(): SubjectFields {
  return { kind: "allClients", uuid: "" };
}

/**
 * A UUID is exactly eight digits (migration 030), so the box takes eight and
 * no more: digits only, never chunked, never signed.
 */
export const UUID_DIGITS = 8;

export function cleanUuid(typed: string): string {
  return typed.replace(/\D/g, "").slice(0, UUID_DIGITS);
}

/**
 * Whether what is typed is a whole UUID yet.
 *
 * Eight digits is the whole number, so it is also the signal that someone has
 * finished typing. Asking the server before then would fire a request per
 * keystroke and flash "UUID not found" at somebody who is still typing.
 */
export function isCompleteUuid(typed: string): boolean {
  return cleanUuid(typed).length === UUID_DIGITS;
}

/**
 * The subject to ask the server for.
 *
 * The three choices are exclusive, so they are radios: a group, or one
 * person. The UUID box only means anything on `user`, and is only editable
 * there. A half-typed number is not an error and not a request: the screen
 * asks once eight digits are in, and until then shows all clients.
 */
export function subjectOf({ kind, uuid }: SubjectFields): FeatureSubject {
  if (kind === "user" && isCompleteUuid(uuid)) return { kind: "user", uuid: Number(cleanUuid(uuid)) };
  return { kind: kind === "allPros" ? "allPros" : "allClients" };
}

/** Whether two subjects name the same thing, so the table only reloads when it must. */
export function sameSubject(one: FeatureSubject, other: FeatureSubject): boolean {
  if (one.kind !== other.kind) return false;
  return one.kind !== "user" || other.kind !== "user" || one.uuid === other.uuid;
}

// ── What the rows say ────────────────────────────────────────────────────

/** Where a row's value came from, in the reader's words. */
export function sourceLabel(source: CustomerSettingSource | undefined, subject: FeatureSubject): string {
  if (source === undefined || source === "default") return t("features.source.default");
  if (source === "user") {
    return subject.kind === "user" ? t("features.source.thisUser") : t("features.source.user");
  }
  const named: Partial<Record<CustomerSettingSource, MessageId>> = {
    allClients: "features.source.allClients",
    allPros: "features.source.allPros",
    proProfile: "features.source.proProfile",
    region: "features.source.region",
    appVersion: "features.source.appVersion",
    system: "features.source.system",
  };
  const id = named[source];
  return id ? t(id) : t("features.source.default");
}

/** Whether this row's value is the subject's own, rather than inherited. */
export function isOwnValue({ source }: Pick<CustomerFeatureListItem, "source">, subject: FeatureSubject): boolean {
  return source === (subject.kind === "user" ? "user" : subject.kind);
}

/** Why a toggle is dead, in the reader's words. Null when it is live. */
export function notEditableLabel({ editable, notEditable }: CustomerFeatureListItem): string | null {
  if (editable !== false) return null;
  return notEditable === "critical_requires_owner" ? t("features.locked.owner") : t("features.locked.scope");
}

// ── The draft ────────────────────────────────────────────────────────────

/**
 * What the toggles say now, for the rows somebody has moved. `null` is the
 * third state: clear the value at this scope and inherit again.
 */
export type Draft = Readonly<Record<string, boolean | null>>;

/** What a row shows: the draft when it has been moved, the loaded value otherwise. */
export function shownValue(feature: CustomerFeatureListItem, draft: Draft): boolean {
  const drafted = draft[feature.settingId];
  if (drafted === undefined) return feature.value ?? feature.defaultValue;
  // Cleared: it will read whatever the broader scope says, which we do not
  // know until the server answers. The default is the honest stand-in.
  return drafted ?? feature.defaultValue;
}

/** Whether this row has been moved since it loaded. */
export function isChanged(feature: CustomerFeatureListItem, draft: Draft): boolean {
  return draft[feature.settingId] !== undefined;
}

/** Flip one row. Moving it back to what it loaded as drops it from the draft. */
export function withToggle(draft: Draft, feature: CustomerFeatureListItem, subject: FeatureSubject): Draft {
  const next = !shownValue(feature, draft);
  return withValue(draft, feature, next, subject);
}

/** Clear one row, so the next broader scope applies again. */
export function withClear(draft: Draft, feature: CustomerFeatureListItem): Draft {
  return { ...draft, [feature.settingId]: null };
}

function withValue(
  draft: Draft,
  feature: CustomerFeatureListItem,
  value: boolean | null,
  subject: FeatureSubject,
): Draft {
  const { [feature.settingId]: _dropped, ...rest } = draft;
  // Back to what it loaded as, and already the subject's own value: there is
  // nothing to save, so it stops being a change at all.
  if (value !== null && value === feature.value && isOwnValue(feature, subject)) return rest;
  return { ...rest, [feature.settingId]: value };
}

/** Throw the draft away. */
export const EMPTY_DRAFT: Draft = {};

export function changedFeatures(
  features: readonly CustomerFeatureListItem[],
  draft: Draft,
): readonly CustomerFeatureListItem[] {
  return features.filter((feature) => isChanged(feature, draft));
}

// ── The Save ─────────────────────────────────────────────────────────────

/**
 * One Save, carrying every row that moved.
 *
 * `valueVersion` travels back untouched: absent when nothing was set at this
 * scope, so the server creates, and present when something was, so the server
 * refuses if somebody else has moved it meanwhile.
 */
export function saveRequest(
  subject: FeatureSubject,
  reason: string,
  features: readonly CustomerFeatureListItem[],
  draft: Draft,
): SaveCustomerFeaturesRequest {
  return {
    subject,
    reason: reason.trim(),
    changes: changedFeatures(features, draft).map(({ settingId, valueVersion }) => ({
      settingId,
      value: draft[settingId] ?? null,
      ...(valueVersion === undefined ? {} : { version: valueVersion }),
    })),
  };
}

/** What is wrong with this Save, before it is sent. Null when it can go. */
export function saveProblem(reason: string, features: readonly CustomerFeatureListItem[], draft: Draft): string | null {
  const changed = changedFeatures(features, draft);
  if (changed.length === 0) return t("features.save.nothing");
  if (changed.some((feature) => feature.editable === false)) return t("features.save.locked");
  if (reason.trim().length < REASON_MIN_LENGTH) return t("features.save.reasonNeeded");
  return null;
}

// ── Narrowing the table ──────────────────────────────────────────────────

/**
 * What the chips above the table are set to.
 *
 * Every one of them narrows what is already loaded: the server sends the whole
 * catalogue for one subject (forty-odd rows), so filtering here costs nothing
 * and keeps the draft intact — a row hidden by a filter is still saved.
 */
export type FeatureFilters = {
  category: SettingCategory | "all";
  /** `own` is the interesting one: rows somebody decided about this subject. */
  source: "any" | "own" | "inherited" | "default";
  value: "any" | "on" | "off";
  search: string;
};

export function emptyFilters(): FeatureFilters {
  return { category: "all", source: "any", value: "any", search: "" };
}

export function hasFilters({ category, source, value, search }: FeatureFilters): boolean {
  return category !== "all" || source !== "any" || value !== "any" || search.trim() !== "";
}

/** Every category the loaded rows actually use, so the chip offers no empty choice. */
export function categoriesOf(features: readonly CustomerFeatureListItem[]): readonly SettingCategory[] {
  return [...new Set(features.map((feature) => feature.category))].sort();
}

function matchesSource(
  feature: CustomerFeatureListItem,
  wanted: FeatureFilters["source"],
  subject: FeatureSubject,
): boolean {
  if (wanted === "any") return true;
  if (wanted === "default") return feature.source === undefined || feature.source === "default";
  const own = isOwnValue(feature, subject);
  return wanted === "own" ? own : !own && feature.source !== "default" && feature.source !== undefined;
}

/**
 * The rows to show. It searches the translated name, not the setting id: the
 * id is for the code, and it is not on the screen to be searched.
 */
export function visibleFeatures(
  features: readonly CustomerFeatureListItem[],
  filters: FeatureFilters,
  subject: FeatureSubject,
  draft: Draft,
  nameOf: (settingId: CustomerFeatureListItem["settingId"]) => string,
): readonly CustomerFeatureListItem[] {
  const needle = filters.search.trim().toLocaleLowerCase();
  return features.filter((feature) => {
    if (filters.category !== "all" && feature.category !== filters.category) return false;
    if (!matchesSource(feature, filters.source, subject)) return false;
    if (filters.value !== "any" && shownValue(feature, draft) !== (filters.value === "on")) return false;
    return needle === "" || nameOf(feature.settingId).toLocaleLowerCase().includes(needle);
  });
}
